import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { formatIDR, formatDate, PLATFORMS, STATUSES } from "../lib/format";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Pencil, Trash2, Download, Search, MessageCircle, Link as LinkIcon, AlertTriangle, FileText, ChevronDown, StickyNote, Lock, Eye, EyeOff, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import IcalImportDialog from "../components/IcalImportDialog";
import { WA_TEMPLATES, inferLanguage } from "../lib/whatsapp";
import { COUNTRIES, flagFor } from "../lib/countries";
import { MONTHS_ID } from "../lib/format";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { useAuth } from "../lib/auth";

// Detect bookings imported from iCal (Booking.com / Airbnb don't share guest data via iCal — privacy policy)
const ICAL_GUEST_NAMES = new Set([
  "blocked", "closed", "not available", "reserved", "unavailable", "ical", "external"
]);
const isIcalBlocked = (b) => {
  if (b?.notes && /imported from|ical/i.test(b.notes)) return true;
  const gn = (b?.guest_name || "").trim().toLowerCase();
  if (ICAL_GUEST_NAMES.has(gn)) return true;
  if (gn.startsWith("closed") || gn.startsWith("blocked") || gn.startsWith("reserved")) return true;
  return false;
};

const empty = {
  guest_name: "",
  platform: "Booking.com",
  check_in: "",
  check_out: "",
  guests: 1,
  rooms_count: 1,
  room_name: "",
  status: "Pending",
  total_price: 0,
  deposit: 0,
  notes: "",
  phone_number: "",
  country: "",
  language: "en",  // Booking.com default
};

const nowYear = new Date().getFullYear();
const nowMonth = new Date().getMonth() + 1;

export default function Bookings() {
  const { isOwner } = useAuth();
  const [rows, setRows] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [deleteId, setDeleteId] = useState(null);
  const [icalOpen, setIcalOpen] = useState(false);
  const [conflictInfo, setConflictInfo] = useState(null);
  const [priceBreakdown, setPriceBreakdown] = useState(null);

  const [fStatus, setFStatus] = useState("All");
  const [fPlatform, setFPlatform] = useState("All");
  const [fRoom, setFRoom] = useState("All");
  const [fMonth, setFMonth] = useState("All");
  const [fYear, setFYear] = useState(String(nowYear));
  const [fCountry, setFCountry] = useState("All");
  const [search, setSearch] = useState("");
  const [showBlocked, setShowBlocked] = useState(false);  // hide iCal blocked dates by default

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fStatus !== "All") params.append("status", fStatus);
      if (fPlatform !== "All") params.append("platform", fPlatform);
      if (fRoom !== "All") params.append("room", fRoom);
      if (search) params.append("search", search);
      const [b, r] = await Promise.all([
        api.get(`/bookings?${params.toString()}`),
        api.get(`/rooms`),
      ]);
      setRows(b.data);
      setRooms(r.data);
    } finally {
      setLoading(false);
    }
  };

  // Memoized: split rows into blocked (iCal) vs active, apply month/year filter, then sort by proximity to today
  const { displayRows, blockedCount } = useMemo(() => {
    const blocked = rows.filter(isIcalBlocked);
    let list = showBlocked ? [...rows] : rows.filter((r) => !isIcalBlocked(r));

    // Month/Year filter on check_in
    if (fYear !== "All") {
      list = list.filter((r) => (r.check_in || "").startsWith(`${fYear}-`));
    }
    if (fMonth !== "All") {
      const mm = String(fMonth).padStart(2, "0");
      list = list.filter((r) => {
        const ci = r.check_in || "";
        if (fYear === "All") return ci.slice(5, 7) === mm;
        return ci.startsWith(`${fYear}-${mm}-`);
      });
    }

    // Country filter
    if (fCountry !== "All") {
      list = list.filter((r) => (r.country || "") === fCountry);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();
    list.sort((a, b) => {
      const da = a.check_in ? new Date(a.check_in).getTime() : 0;
      const db = b.check_in ? new Date(b.check_in).getTime() : 0;
      const aFuture = da >= todayMs;
      const bFuture = db >= todayMs;
      if (aFuture && !bFuture) return -1;
      if (!aFuture && bFuture) return 1;
      if (aFuture && bFuture) return da - db;
      return db - da;
    });
    return { displayRows: list, blockedCount: blocked.length };
  }, [rows, showBlocked, fMonth, fYear, fCountry]);

  // Available countries from current bookings (for filter dropdown)
  const availableCountries = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => r.country && !isIcalBlocked(r) && set.add(r.country));
    return Array.from(set).sort();
  }, [rows]);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [fStatus, fPlatform, fRoom]);

  useEffect(() => {
    const h = setTimeout(load, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line
  }, [search]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...empty, room_name: rooms[0]?.name || "" });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      guest_name: row.guest_name,
      platform: row.platform,
      check_in: row.check_in,
      check_out: row.check_out,
      guests: row.guests,
      rooms_count: row.rooms_count || 1,
      room_name: row.room_name,
      status: row.status,
      total_price: row.total_price,
      deposit: row.deposit || 0,
      notes: row.notes || "",
      phone_number: row.phone_number || "",
      country: row.country || "",
      language: row.language || (row.platform === "Booking.com" || row.platform === "Airbnb" ? "en" : "id"),
    });
    setDialogOpen(true);
  };

  // Auto-suggest language when platform changes (only for new bookings or if user hasn't manually set it)
  const handlePlatformChange = (newPlatform) => {
    const suggestedLang = (newPlatform === "Booking.com" || newPlatform === "Airbnb") ? "en" : "id";
    setForm((prev) => ({ ...prev, platform: newPlatform, language: suggestedLang }));
  };

  const save = async (forceOverride = false) => {
    if (!form.guest_name || !form.check_in || !form.check_out || !form.room_name) {
      toast.error("Mohon lengkapi nama tamu, tanggal, dan kamar.");
      return;
    }
    if (new Date(form.check_out) <= new Date(form.check_in)) {
      toast.error("Tanggal check-out harus setelah check-in.");
      return;
    }
    try {
      const payload = {
        ...form,
        guests: Number(form.guests),
        rooms_count: Math.max(1, Number(form.rooms_count) || 1),
        total_price: Number(form.total_price),
        deposit: Number(form.deposit),
        force: forceOverride,
      };
      if (editing) {
        await api.put(`/bookings/${editing.id}`, payload);
        toast.success("Booking diperbarui.");
      } else {
        await api.post(`/bookings`, payload);
        toast.success("Booking berhasil dibuat.");
      }
      setDialogOpen(false);
      setConflictInfo(null);
      load();
    } catch (e) {
      if (e.response?.status === 409) {
        setConflictInfo({ message: e.response.data.detail });
      } else {
        toast.error(e.response?.data?.detail || "Gagal menyimpan booking.");
      }
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/bookings/${deleteId}`);
      toast.success("Booking dihapus.");
      setDeleteId(null);
      load();
    } catch {
      toast.error("Gagal menghapus.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/export/csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "meos-ambower-bookings.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Gagal export CSV.");
    }
  };

  const nights = useMemo(() => {
    if (!form.check_in || !form.check_out) return 0;
    const d1 = new Date(form.check_in);
    const d2 = new Date(form.check_out);
    return Math.max(Math.round((d2 - d1) / (1000 * 60 * 60 * 24)), 0);
  }, [form.check_in, form.check_out]);

  // Auto-calculate price from room + dates using season rules
  useEffect(() => {
    if (!form.room_name || !form.check_in || !form.check_out || nights <= 0) {
      setPriceBreakdown(null);
      return;
    }
    const handler = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          room_name: form.room_name,
          check_in: form.check_in,
          check_out: form.check_out,
        });
        const r = await api.get(`/pricing/calculate?${params.toString()}`);
        setPriceBreakdown(r.data);
      } catch {
        setPriceBreakdown(null);
      }
    }, 250);
    return () => clearTimeout(handler);
  }, [form.room_name, form.check_in, form.check_out, nights]);

  const applyCalculatedPrice = () => {
    if (priceBreakdown?.subtotal) {
      setForm((f) => ({ ...f, total_price: priceBreakdown.subtotal }));
      toast.success(`Harga auto-diterapkan: ${formatIDR(priceBreakdown.subtotal)}`);
    }
  };

  return (
    <>
      <PageHeader
        title="Bookings"
        subtitle="Kelola seluruh booking homestay — tambah, edit, hapus, dan export data."
        actions={
          <>
            <Button
              data-testid="btn-import-ical"
              variant="outline"
              onClick={() => setIcalOpen(true)}
              className="bg-white border-[#EBE6DE] hover:border-[#D16B4D] hover:text-[#D16B4D] text-[#172E21] rounded-xl"
            >
              <LinkIcon className="h-4 w-4 mr-2" />
              Import iCal
            </Button>
            <Button
              data-testid="btn-export-csv"
              variant="outline"
              onClick={exportCsv}
              className="bg-white border-[#EBE6DE] hover:border-[#D16B4D] hover:text-[#D16B4D] text-[#172E21] rounded-xl"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Link
              data-testid="btn-print-bookings-link"
              to={`/bookings/print?status=${fStatus}&platform=${fPlatform}&room=${encodeURIComponent(fRoom)}&month=${fMonth}&year=${fYear}&country=${encodeURIComponent(fCountry)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                className="bg-white border-[#EBE6DE] hover:border-[#D16B4D] hover:text-[#D16B4D] text-[#172E21] rounded-xl"
              >
                <Printer className="h-4 w-4 mr-2" />
                Cetak Daftar
              </Button>
            </Link>
            <Button
              data-testid="btn-add-booking"
              onClick={openCreate}
              className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl shadow-md shadow-[#D16B4D]/20"
            >
              <Plus className="h-4 w-4 mr-2" />
              Tambah Booking
            </Button>
          </>
        }
      />

      {/* Filters */}
      <div className="bg-white border border-[#EBE6DE] rounded-2xl p-4 shadow-sm flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#586D61]" />
          <Input
            data-testid="input-search-guest"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama tamu…"
            className="pl-9 border-[#EBE6DE]"
          />
        </div>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="filter-status" className="w-[160px] bg-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">Semua Status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fPlatform} onValueChange={setFPlatform}>
          <SelectTrigger data-testid="filter-platform" className="w-[180px] bg-white">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">Semua Platform</SelectItem>
            {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fRoom} onValueChange={setFRoom}>
          <SelectTrigger data-testid="filter-room" className="w-[200px] bg-white">
            <SelectValue placeholder="Kamar" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">Semua Kamar</SelectItem>
            {rooms.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={fMonth} onValueChange={setFMonth}>
          <SelectTrigger data-testid="filter-month" className="w-[160px] bg-white">
            <SelectValue placeholder="Bulan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">Semua Bulan</SelectItem>
            {MONTHS_ID.map((m, i) => (
              <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={fYear} onValueChange={setFYear}>
          <SelectTrigger data-testid="filter-year" className="w-[110px] bg-white">
            <SelectValue placeholder="Tahun" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">Semua</SelectItem>
            {[nowYear - 1, nowYear, nowYear + 1].map((y) => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {availableCountries.length > 0 && (
          <Select value={fCountry} onValueChange={setFCountry}>
            <SelectTrigger data-testid="filter-country" className="w-[200px] bg-white">
              <SelectValue placeholder="Negara" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="All">🌍 Semua Negara</SelectItem>
              {availableCountries.map((c) => (
                <SelectItem key={c} value={c}>{flagFor(c)} {c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {(fMonth !== "All" || fYear !== String(nowYear)) && (
          <Button
            data-testid="btn-clear-date-filter"
            type="button"
            variant="ghost"
            onClick={() => { setFMonth("All"); setFYear(String(nowYear)); }}
            className="text-[#586D61] hover:text-[#D16B4D] rounded-xl h-10 px-3 text-xs"
          >
            Reset periode
          </Button>
        )}

        {blockedCount > 0 && (
          <Button
            data-testid="btn-toggle-blocked"
            type="button"
            variant="outline"
            onClick={() => setShowBlocked((v) => !v)}
            title={showBlocked ? "Sembunyikan tanggal blocked dari iCal" : "Tampilkan tanggal blocked dari iCal (Booking.com/Airbnb)"}
            className="rounded-xl border-[#EBE6DE] text-[#586D61] hover:bg-[#FDFBF7]"
          >
            {showBlocked ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            {showBlocked ? "Sembunyikan" : "Tampilkan"} Blocked
            <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
              {blockedCount}
            </span>
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-[#EBE6DE] rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FDFBF7] hover:bg-[#FDFBF7]">
                <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Tamu</TableHead>
                <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Platform</TableHead>
                <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Check-in</TableHead>
                <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Check-out</TableHead>
                <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-center">Malam</TableHead>
                <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-center">Tamu</TableHead>
                <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Kamar</TableHead>
                <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Status</TableHead>
                <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">Total</TableHead>
                <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">DP</TableHead>
                <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={11} className="text-center text-[#586D61] py-10">Memuat…</TableCell></TableRow>
              )}
              {!loading && displayRows.length === 0 && (
                <TableRow><TableCell colSpan={11} className="text-center text-[#586D61] py-10">Belum ada booking. Klik "Tambah Booking".</TableCell></TableRow>
              )}
              {displayRows.map((r) => {
                const blocked = isIcalBlocked(r);
                return (
                <TableRow
                  key={r.id}
                  data-testid={`booking-row-${r.id}`}
                  className={blocked ? "bg-[#FDFBF7]/50 hover:bg-[#FDFBF7] opacity-75" : "hover:bg-[#FDFBF7]"}
                >
                  <TableCell className={blocked ? "font-medium text-[#586D61]" : "font-medium text-[#172E21]"}>
                    <div className="flex items-center gap-1.5">
                      {blocked && (
                        <span
                          title="Tanggal diblokir oleh iCal sync (Booking.com/Airbnb tidak share data tamu)"
                          className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-stone-200 text-stone-600 flex-shrink-0"
                        >
                          <Lock className="h-3 w-3" />
                        </span>
                      )}
                      {!blocked && r.country && (
                        <span title={r.country} className="text-base leading-none flex-shrink-0" data-testid={`country-flag-${r.id}`}>
                          {flagFor(r.country)}
                        </span>
                      )}
                      <span className={blocked ? "italic" : ""}>{blocked ? "Blocked Date" : r.guest_name}</span>
                      {!blocked && r.notes && (
                        <span
                          data-testid={`booking-note-icon-${r.id}`}
                          title={r.notes}
                          className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-100 text-amber-700 flex-shrink-0"
                        >
                          <StickyNote className="h-3 w-3" />
                        </span>
                      )}
                    </div>
                    {!blocked && r.notes && <div className="text-xs text-[#586D61] mt-0.5 max-w-[220px] truncate" title={r.notes}>{r.notes}</div>}
                    {blocked && <div className="text-xs text-stone-500 mt-0.5">{r.notes || "Imported from iCal"}</div>}
                  </TableCell>
                  <TableCell className="text-sm text-[#586D61]">{r.platform}</TableCell>
                  <TableCell className="text-sm">{formatDate(r.check_in)}</TableCell>
                  <TableCell className="text-sm">{formatDate(r.check_out)}</TableCell>
                  <TableCell className="text-center font-mono text-sm">{r.nights}</TableCell>
                  <TableCell className="text-center font-mono text-sm">{r.guests}</TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-1.5">
                      <span>{r.room_name}</span>
                      {(r.rooms_count || 1) > 1 && (
                        <span
                          data-testid={`rooms-count-${r.id}`}
                          title={`${r.rooms_count} kamar dipesan`}
                          className="inline-flex items-center justify-center px-1.5 h-5 rounded-full bg-[#D16B4D]/15 text-[#D16B4D] text-[10px] font-bold"
                        >
                          ×{r.rooms_count}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatIDR(r.total_price)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-[#586D61]">{formatIDR(r.deposit || 0)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        data-testid={`btn-invoice-${r.id}`}
                        variant="ghost" size="icon"
                        onClick={() => window.open(`/invoice/${r.id}`, "_blank")}
                        title="Buka invoice (print/PDF)"
                        className="h-8 w-8 hover:bg-[#D16B4D]/10 hover:text-[#D16B4D]"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      {r.phone_number ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              data-testid={`btn-whatsapp-${r.id}`}
                              variant="ghost" size="icon"
                              title="Kirim pesan WhatsApp"
                              className="h-8 w-8 hover:bg-[#25D366]/10 hover:text-[#25D366] relative"
                            >
                              <MessageCircle className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-72" data-testid={`wa-menu-${r.id}`}>
                            <DropdownMenuLabel className="text-xs text-[#586D61] flex items-center justify-between">
                              <span>Pilih Template Pesan</span>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-[#D16B4D]">
                                {(r.language || inferLanguage(r)) === "en" ? "🇬🇧 EN" : "🇮🇩 ID"}
                              </span>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {WA_TEMPLATES.map((t) => {
                              const lang = r.language || inferLanguage(r);
                              const label = typeof t.label === "string" ? t.label : (t.label[lang] || t.label.id);
                              return (
                                <DropdownMenuItem
                                  key={t.key}
                                  data-testid={`wa-template-${t.key}-${r.id}`}
                                  onClick={() => window.open(t.url(r, lang), "_blank")}
                                  className="cursor-pointer gap-2.5 py-2.5"
                                >
                                  <span className="text-base">{t.icon}</span>
                                  <span className="text-sm font-medium flex-1">{label}</span>
                                </DropdownMenuItem>
                              );
                            })}
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-[10px] text-[#586D61] font-normal italic px-3 py-1.5">
                              Ubah bahasa di form Edit Booking
                            </DropdownMenuLabel>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Button
                          variant="ghost" size="icon" disabled
                          title="Tambahkan nomor HP tamu untuk mengaktifkan WA"
                          className="h-8 w-8 opacity-30"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        data-testid={`btn-edit-booking-${r.id}`}
                        variant="ghost" size="icon"
                        onClick={() => openEdit(r)}
                        className="h-8 w-8 hover:bg-[#2C7A51]/10 hover:text-[#2C7A51]"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {isOwner && (
                        <Button
                          data-testid={`btn-delete-booking-${r.id}`}
                          variant="ghost" size="icon"
                          onClick={() => setDeleteId(r.id)}
                          className="h-8 w-8 hover:bg-[#D14D4D]/10 hover:text-[#D14D4D]"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-white" data-testid="booking-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#172E21]">
              {editing ? "Edit Booking" : "Tambah Booking Baru"}
            </DialogTitle>
            <DialogDescription className="text-[#586D61]">
              Isi detail booking tamu di bawah ini.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="md:col-span-2">
              <Label className="text-[#172E21] text-sm font-medium">Nama Tamu</Label>
              <Input
                data-testid="input-guest-name"
                value={form.guest_name}
                onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                className="mt-1.5 border-[#EBE6DE]"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[#172E21] text-sm font-medium">Nomor WhatsApp <span className="text-[#586D61] font-normal">(opsional, untuk kirim konfirmasi)</span></Label>
              <Input
                data-testid="input-phone-number"
                value={form.phone_number}
                onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                placeholder="081234567890 atau +628123..."
                className="mt-1.5 border-[#EBE6DE] font-mono"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[#172E21] text-sm font-medium">Asal Negara <span className="text-[#586D61] font-normal">(opsional)</span></Label>
              <Select
                value={form.country || "__none__"}
                onValueChange={(v) => setForm({ ...form, country: v === "__none__" ? "" : v })}
              >
                <SelectTrigger data-testid="select-country" className="mt-1.5">
                  <SelectValue placeholder="Pilih negara asal tamu" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none__">— Tidak disebut —</SelectItem>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.name}>
                      <span className="mr-2">{c.flag}</span>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#172E21] text-sm font-medium">Platform</Label>
              <Select value={form.platform} onValueChange={handlePlatformChange}>
                <SelectTrigger data-testid="select-platform" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#172E21] text-sm font-medium">
                Bahasa Komunikasi <span className="text-[#586D61] font-normal text-xs">(WA & Invoice)</span>
              </Label>
              <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                <SelectTrigger data-testid="select-language" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="id">🇮🇩 Indonesia</SelectItem>
                  <SelectItem value="en">🇬🇧 English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#172E21] text-sm font-medium">Kamar / Bungalow</Label>
              <Select value={form.room_name} onValueChange={(v) => setForm({ ...form, room_name: v })}>
                <SelectTrigger data-testid="select-room" className="mt-1.5"><SelectValue placeholder="Pilih kamar" /></SelectTrigger>
                <SelectContent>{rooms.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#172E21] text-sm font-medium">Check-in</Label>
              <Input
                data-testid="input-check-in"
                type="date"
                value={form.check_in}
                onChange={(e) => setForm({ ...form, check_in: e.target.value })}
                className="mt-1.5 border-[#EBE6DE]"
              />
            </div>
            <div>
              <Label className="text-[#172E21] text-sm font-medium">Check-out</Label>
              <Input
                data-testid="input-check-out"
                type="date"
                value={form.check_out}
                onChange={(e) => setForm({ ...form, check_out: e.target.value })}
                className="mt-1.5 border-[#EBE6DE]"
              />
            </div>
            <div>
              <Label className="text-[#172E21] text-sm font-medium">Jumlah Malam (auto)</Label>
              <Input value={nights} readOnly className="mt-1.5 bg-[#FDFBF7] border-[#EBE6DE] font-mono" />
            </div>
            <div>
              <Label className="text-[#172E21] text-sm font-medium">Jumlah Tamu</Label>
              <Input
                data-testid="input-guests"
                type="number" min={1}
                value={form.guests}
                onChange={(e) => setForm({ ...form, guests: e.target.value })}
                className="mt-1.5 border-[#EBE6DE]"
              />
            </div>
            <div>
              <Label className="text-[#172E21] text-sm font-medium">
                Jumlah Kamar <span className="text-[#586D61] font-normal">(jika lebih dari 1)</span>
              </Label>
              <Input
                data-testid="input-rooms-count"
                type="number" min={1} max={20}
                value={form.rooms_count}
                onChange={(e) => setForm({ ...form, rooms_count: e.target.value })}
                className="mt-1.5 border-[#EBE6DE]"
              />
            </div>
            <div>
              <Label className="text-[#172E21] text-sm font-medium">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="select-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#172E21] text-sm font-medium">Total Harga (IDR)</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  data-testid="input-total-price"
                  type="number" min={0}
                  value={form.total_price}
                  onChange={(e) => setForm({ ...form, total_price: e.target.value })}
                  className="border-[#EBE6DE] font-mono"
                />
                {priceBreakdown?.subtotal > 0 && (
                  <Button
                    data-testid="btn-apply-calc-price"
                    type="button"
                    variant="outline"
                    onClick={applyCalculatedPrice}
                    className="flex-shrink-0 border-[#D16B4D] text-[#D16B4D] hover:bg-[#D16B4D] hover:text-white rounded-xl text-xs whitespace-nowrap"
                  >
                    Auto {formatIDR(priceBreakdown.subtotal)}
                  </Button>
                )}
              </div>
              {priceBreakdown?.breakdown?.length > 0 && priceBreakdown.breakdown.some((b) => b.multiplier > 1) && (
                <div className="text-xs text-[#D99338] mt-1 flex items-center gap-1">
                  <span className="font-bold">●</span>
                  High season ({priceBreakdown.breakdown.find((b) => b.multiplier > 1)?.season}) — multiplier {priceBreakdown.breakdown[0].multiplier}×
                </div>
              )}
            </div>
            <div>
              <Label className="text-[#172E21] text-sm font-medium">DP / Pembayaran (IDR)</Label>
              <Input
                data-testid="input-deposit"
                type="number" min={0}
                value={form.deposit}
                onChange={(e) => setForm({ ...form, deposit: e.target.value })}
                className="mt-1.5 border-[#EBE6DE] font-mono"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[#172E21] text-sm font-medium">Catatan Khusus</Label>
              <Textarea
                data-testid="input-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1.5 border-[#EBE6DE]"
                placeholder="Permintaan khusus, alergi makanan, dll."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl" data-testid="btn-cancel-booking">
              Batal
            </Button>
            <Button
              data-testid="btn-save-booking"
              onClick={() => save(false)}
              className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl"
            >
              {editing ? "Simpan Perubahan" : "Buat Booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl">Hapus Booking?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#586D61]">
              Data booking akan dihapus permanen dan tidak dapat dikembalikan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-delete" className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-delete"
              onClick={doDelete}
              className="bg-[#D14D4D] hover:bg-[#B83E3E] text-white rounded-xl"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Conflict Dialog */}
      <AlertDialog open={!!conflictInfo} onOpenChange={(v) => !v && setConflictInfo(null)}>
        <AlertDialogContent className="bg-white" data-testid="conflict-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl flex items-center gap-2 text-[#D99338]">
              <AlertTriangle className="h-5 w-5" />
              Konflik Jadwal Terdeteksi
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#586D61]">
              {conflictInfo?.message}
              <br /><br />
              Apakah Anda tetap ingin menyimpan booking ini? Overlap dapat menyebabkan double-booking pada kamar yang sama.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-force-save"
              onClick={() => save(true)}
              className="bg-[#D99338] hover:bg-[#B8791F] text-white rounded-xl"
            >
              Tetap Simpan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <IcalImportDialog
        open={icalOpen}
        onOpenChange={setIcalOpen}
        onImported={load}
      />
    </>
  );
}
